import { useState, useEffect, useCallback } from "react";
import { 
  Globe, 
  ExternalLink, 
  Copy, 
  Check,
  Trash2,
  Search,
  Calendar,
  Building,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

const INDUSTRY_LABELS = {
  bygg: "Byggföretag",
  vvs: "Rörmokare / VVS",
  elektriker: "Elektriker",
  malare: "Målerifirma",
  stad: "Städfirma",
  flytt: "Flyttfirma",
  frisor: "Frisörsalong",
  veterinar: "Veterinärklinik",
  mark: "Markentreprenad",
  glas: "Glasmästeri",
  tradgard: "Trädgårdstjänster",
  tak: "Takläggare",
  snickare: "Snickeri",
  plattsattning: "Plattsättning"
};

const ITEMS_PER_PAGE = 20;

export default function DemosList() {
  const { getAuthHeaders } = useAuth();
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    totalCount: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  });

  const loadDemos = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/demos?page=${page}&limit=${ITEMS_PER_PAGE}&sort_by=created_at&sort_order=desc`,
        { headers: getAuthHeaders(), credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setDemos(data.demos);
        if (data.pagination) {
          setPagination({
            page: data.pagination.page,
            totalCount: data.pagination.total_count,
            totalPages: data.pagination.total_pages,
            hasNext: data.pagination.has_next,
            hasPrev: data.pagination.has_prev
          });
        }
      }
    } catch (error) {
      console.error("Failed to load demos:", error);
      toast.error("Kunde inte ladda demos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadDemos(1);
  }, [loadDemos]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadDemos(newPage);
    }
  };

  const handleCopyLink = (demo) => {
    // Always use elido.nu for demo links
    const demoUrl = `https://elido.nu/d/${demo.slug}`;
    navigator.clipboard.writeText(demoUrl);
    setCopiedId(demo.id);
    toast.success("Länk kopierad!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleOpenDemo = (demo) => {
    // Open demo using the new AI demo URL structure
    window.open(`/d/${demo.slug}`, "_blank");
  };

  const handleDelete = async (demoId) => {
    if (!window.confirm("Är du säker på att du vill ta bort denna demo?")) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/demos/${demoId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        setDemos(demos.filter(d => d.id !== demoId));
        toast.success("Demo borttagen");
      }
    } catch (error) {
      toast.error("Kunde inte ta bort demo");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const filteredDemos = demos.filter(demo => 
    demo.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    demo.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Globe className="w-7 h-7 text-blue-600" />
            Demos
          </h1>
          <p className="mt-1 text-gray-600">
            {demos.length} demo{demos.length !== 1 ? "s" : ""} skapade
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Sök företag eller stad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 w-full sm:w-64"
          />
        </div>
      </div>

      {/* Demos Table */}
      {filteredDemos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? "Inga matchande demos" : "Inga demos ännu"}
          </h3>
          <p className="text-gray-500">
            {searchTerm ? "Prova en annan sökning" : "Skapa din första demo med AI Demo Builder"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Företag</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Bransch</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Stad</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Webbplats</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Skapad</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold text-gray-600">Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {filteredDemos.map((demo) => (
                  <tr key={demo.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{demo.company_name}</p>
                          <p className="text-xs text-gray-500 font-mono">{demo.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                        {INDUSTRY_LABELS[demo.industry] || demo.industry}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {demo.city}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {demo.website_url ? (
                        <a 
                          href={demo.website_url.startsWith("http") ? demo.website_url : `https://${demo.website_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm truncate max-w-[150px] block"
                        >
                          {demo.website_url}
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(demo.created_at)}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleCopyLink(demo)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Kopiera länk"
                        >
                          {copiedId === demo.id ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleOpenDemo(demo)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Öppna demo"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(demo.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Ta bort"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <div className="text-sm text-gray-500">
                Visar {((pagination.page - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(pagination.page * ITEMS_PER_PAGE, pagination.totalCount)} av {pagination.totalCount} demos
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev || loading}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Föregående
                </button>
                <span className="px-3 py-2 text-sm text-gray-600">
                  Sida {pagination.page} av {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext || loading}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Nästa
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
